import moment from 'moment';

import {
  timeLapse as timeLapseConstant,
  timeLapseOptions
} from '../pages/dashboard/constants';
import config from '../constants';

export const getCurrentDay = () => {
  return new Date();
};

export const substractDays = (day, value) => {
  return day.setDate(day.getDate() - value);
};

export const daysBetween = (date1, date2) => {
  const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
  const firstDate = new Date(date1);
  const secondDate = new Date(date2);
  return Math.round(Math.abs((firstDate - secondDate) / oneDay));
};

export const getTimePeriod = (
  createdAt,
  reportByDateAndMetricType,
  timeLapse,
  timeLapseFrom = null,
  timeLapseTo = null
) => {
  let dateTo = null;
  let dateFrom = null;

  if (timeLapse !== timeLapseConstant[reportByDateAndMetricType].customRange) {
    dateTo = getCurrentDay();
    dateFrom = timeLapseOptions[reportByDateAndMetricType][timeLapse].value
      ? substractDays(
        getCurrentDay(),
        timeLapseOptions[reportByDateAndMetricType][timeLapse].value
      )
      : new Date(createdAt);
  } else {
    dateTo =
      timeLapseTo && moment(timeLapseTo).isValid()
        ? timeLapseTo
        : getCurrentDay();
    dateFrom =
      timeLapseFrom && moment(timeLapseFrom).isValid()
        ? timeLapseFrom
        : new Date(createdAt);
  }

  return {
    dateFrom: moment(dateFrom).format(config.DEFAULT_DATE_FORMAT),
    dateTo: moment(dateTo).format(config.DEFAULT_DATE_FORMAT)
  };
};

export const convertDateAndTimeToDateString = (date, time) => {
  if (!date) {
    return time;
  }

  return `${date}T${time ? time : '00:00'}:00.000`;
};

// Pass in the number of years to go back (default is current year)
export const getCurrentFinancialYear = function(yearsBack) {
  var financialYearStart, financialYearEnd;
  if (moment().quarter() > 2) {
    financialYearStart = moment().month('July').startOf('month'); // this year's July
    financialYearEnd = moment().add(1, 'years').month('June').endOf('month'); // next year's June
  } else {
    financialYearStart = moment()
      .subtract(1, 'years')
      .month('July')
      .startOf('month'); // last year's July
    financialYearEnd = moment().month('June').endOf('month'); // this year's June
  }

  if (!yearsBack) {
    financialYearStart = financialYearStart.subtract(yearsBack, 'years');
    financialYearEnd = financialYearEnd.subtract(yearsBack, 'years');
  }

  return { start: financialYearStart, end: financialYearEnd };
};

/**
 * Map of country codes to their preferred date formats for display
 * Each format is optimized for readability and local conventions
 */
const COUNTRY_DATE_FORMATS = {
  AU: 'DD MMM YYYY',     // Australia: 29 Mar 2025
  NZ: 'DD MMM YYYY',     // New Zealand: 29 Mar 2025  
  US: 'MMM DD, YYYY',    // United States: Mar 29, 2025
  CA: 'MMM DD, YYYY',    // Canada: Mar 29, 2025
  GB: 'DD MMM YYYY',     // United Kingdom: 29 Mar 2025
  // Default fallback format for unsupported countries
  DEFAULT: 'DD MMM YYYY'
};

/**
 * Formats a date string according to the specified country's conventions
 * 
 * @param {string|Date|moment} date - The date to format (supports various input types)
 * @param {string} countryCode - Two-character country code (e.g., 'AU', 'US', 'NZ')
 * @returns {string} Formatted date string according to country conventions
 * 
 * @example
 * formatDateByCountry('2025-03-29', 'AU') // Returns: '29 Mar 2025'
 * formatDateByCountry('2025-03-29', 'US') // Returns: 'Mar 29, 2025'
 * formatDateByCountry('2025-03-29', 'NZ') // Returns: '29 Mar 2025'
 * formatDateByCountry('2025-03-29', 'XX') // Returns: '29 Mar 2025' (default format)
 */
export const formatDateByCountry = (date, countryCode) => {
  // Validate input date
  if (!date || !moment(date).isValid()) {
    return '';
  }

  // Normalize country code to uppercase
  const normalizedCountryCode = countryCode ? countryCode.toUpperCase() : '';

  // Get the appropriate format for the country, fallback to default
  const format = COUNTRY_DATE_FORMATS[normalizedCountryCode] || COUNTRY_DATE_FORMATS.DEFAULT;

  // Format and return the date
  return moment(date).format(format);
};

export const formatDateTime = (date) => {
  const dateTimeFormat = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });

  return dateTimeFormat.format(date);
}
